/**
 * 合并图层（DESIGN.md §7.1 补充：图层面板多选右键"合并图层"，参照
 * Photoshop）。跟"拼合图像"（`app/flatten.ts`）同一套机制，区别只是
 * 只对选中的这几个图层的子集合成，其余图层原样保留。
 */
import { layerDefaults } from '../document/draft';
import type { RasterLayer } from '../document/types';
import { layerId, type LayerId } from '../engine/tile/key';
import { compositeLayerSubset } from './composite';
import { writeImageIntoTiles } from './rasterize';
import { createMergeLayersCommand } from './commands/layer-commands';
import { computeTopmostInsertPosition } from './layer-edit';
import type { AppStore } from './store';

/**
 * 合并 `memberIds` 这几个图层为一个新的像素图层，落在其中最上面那个的
 * 位置，其余图层原样保留。返回是否真的执行了——id 不足两个、有 id 已经
 * 不在文档里、或者不是同一父组下的兄弟图层（跨组合并不支持，见
 * `computeTopmostInsertPosition`）时 false。
 */
export function mergeSelectedLayers(
  store: AppStore,
  memberIds: readonly LayerId[],
  nextLayerId: () => string,
): boolean {
  const document = store.document;
  if (document === null || memberIds.length < 2) return false;
  const position = computeTopmostInsertPosition(document, memberIds);
  if (position === null) return false;

  const composite = compositeLayerSubset(document, store.tiles, memberIds);
  const id = layerId(nextLayerId());
  const transaction = store.beginPixelTransaction('合并图层');
  const bounds = writeImageIntoTiles(store, id, composite, transaction);
  const merged: RasterLayer = {
    ...layerDefaults(id, '合并图层'),
    kind: 'raster',
    tiles: store.tiles.handle(id),
    bounds,
  };
  store.dispatchWithPixels(
    createMergeLayersCommand({ memberIds, merged, parentId: position.parentId, index: position.index }),
    transaction.end(),
  );
  store.setActiveLayer(id);
  return true;
}
